import { Alert, Box, Button, Typography } from '@mui/material';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import { BookingFlowResumer } from '../../../booking-flow/BookingFlowResumer';
import { BookingFlowStepper } from '../../../booking-flow/BookingFlowStepper';
import { rentCarFlowConfig, type RentCarFlowState } from '../flowConfig';
import { rentCarResumeAdapter } from '../resume';
import { SearchFormStep } from '../components/SearchFormStep';
import { VehicleResultsStep } from '../components/VehicleResultsStep';
import { DriverFormStep } from '../components/DriverFormStep';
import { PaymentForm } from '../../../components/payment/PaymentForm';
import { SendForApprovalPanel } from '../../../components/booking/SendForApprovalPanel';
import { ApprovalPassengerStep } from '../../../components/booking/ApprovalPassengerStep';
import {
  approvalEmployeeId,
  corporateMaxBudget,
  needsCorporateApproval,
} from '../../../components/booking/corporateApproval';
import { useSentForApproval } from '../../../components/booking/useSentForApproval';
import { useAuthStore } from '../../../auth/authStore';
import { DocumentStep } from '../components/DocumentStep';

function RentCarFlowInner() {
  const flow = useBookingFlowContext<RentCarFlowState>();
  const running = flow.status === 'running';
  const sessionEmployeeId = useAuthStore((s) => s.employeeId);

  useSentForApproval({
    sent: !!flow.state.sentForApproval,
    idle: flow.status === 'idle',
    currentStepId: flow.currentStep?.id,
    passengerStepId: 'driver',
    reservationPath: '/rent-car/reservations',
    reset: flow.reset,
    message:
      'Araç kiralama onaya gönderilmiştir. Yöneticiniz onayladığında rezervasyon listenizden ödemeye devam edebilirsiniz.',
  });

  const gate = {
    cart: flow.state.cart,
    searchEmployeeId: flow.state.searchRequest?.employeeID,
  };
  const approvalEmpId = approvalEmployeeId(gate, sessionEmployeeId);
  const driverStepIsApproval = needsCorporateApproval(gate) && !!approvalEmpId;

  return (
    <Box>
      <BookingFlowStepper />
      {flow.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {flow.error}
        </Alert>
      )}

      {flow.currentStep.id === 'search' && (
        <SearchFormStep
          loading={running}
          error={flow.status === 'error' ? flow.error : undefined}
          onSubmit={(searchRequest) => {
            flow.runStep({ searchRequest }).catch(() => undefined);
          }}
        />
      )}

      {flow.currentStep.id === 'select' && flow.state.searchResult && (
        <VehicleResultsStep
          searchResult={flow.state.searchResult}
          loading={running}
          onSelect={(vehicle) => {
            flow.runStep({ selectedVehicle: vehicle }).catch(() => undefined);
          }}
          onBackToSearch={() => flow.goToStep(0)}
        />
      )}

      {flow.currentStep.id === 'driver' &&
        (driverStepIsApproval ? (
          <ApprovalPassengerStep
            employeeId={approvalEmpId as number}
            maxBudget={corporateMaxBudget(gate)}
            productLabel="araç kiralama"
            loading={running}
            onSubmit={(passengers, contact) => {
              flow
                .runStep({ driver: { passenger: passengers[0], contact }, sentForApproval: true })
                .catch(() => undefined);
            }}
          />
        ) : (
          <DriverFormStep
            loading={running}
            error={flow.status === 'error' ? flow.error : undefined}
            onSubmit={(driver) => {
              flow.runStep({ driver }).catch(() => undefined);
            }}
          />
        ))}

      {flow.currentStep.id === 'prebooking' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ mb: 2 }}>Ön rezervasyon adımına devam edin.</Typography>
          <Button variant="contained" disabled={running} onClick={() => flow.runStep().catch(() => undefined)}>
            {running ? 'İşleniyor…' : 'Ön Rezervasyon Yap'}
          </Button>
        </Box>
      )}

      {flow.currentStep.id === 'finalize' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ mb: 2 }}>Rezervasyonu kesinleştirin.</Typography>
          <Button variant="contained" disabled={running} onClick={() => flow.runStep().catch(() => undefined)}>
            {running ? 'İşleniyor…' : 'Kesinleştir'}
          </Button>
        </Box>
      )}

      {flow.currentStep.id === 'payment' && (
        <>
          <SendForApprovalPanel product="RentCar" cart={flow.state.cart ?? undefined} />
          <PaymentForm
            amount={flow.state.cart?.remainingTotal}
            currency={flow.state.selectedVehicle?.currency ?? undefined}
            loading={running}
            shoppingCartId={flow.state.shoppingCartId}
            onSubmit={({ paymentForm }) => {
              flow.runStep({ paymentForm }).catch(() => undefined);
            }}
          />
        </>
      )}

      {flow.currentStep.id === 'document' && (
        <DocumentStep
          shoppingCartId={flow.state.shoppingCartId}
          cart={flow.state.cart}
          payment={flow.state.payment}
          onStartOver={flow.reset}
        />
      )}
    </Box>
  );
}

export default function RentCarFlowPage() {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Araç Kiralama
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Arama → Rezervasyona Alma → Sürücü Bilgisi → Ön Rezervasyon → Kesinleştirme → Ödeme → Belge
      </Typography>
      <BookingFlowResumer config={rentCarFlowConfig} adapter={rentCarResumeAdapter}>
        <RentCarFlowInner />
      </BookingFlowResumer>
    </Box>
  );
}
